import { useCallback, useRef } from 'react';

type ClickHandler = (event: React.MouseEvent) => void;

interface ClickHandlers {
  onMouseDown: ClickHandler;
  onMouseMove: ClickHandler;
  onMouseUp: ClickHandler;
}
/**
 * @description Handle click event on draggable element, prevent clicking when dragging
 * @param onClick click event handler
 * @param threshold threshould in pixels when ignore dragging and treat as click
 * @returns Return click event handlers, which should be attached to the draggable element
 */
function useDraggableClickHandler(onClick: ClickHandler, threshold:number = 10): ClickHandlers {
  const startX = useRef(0);
  const startY = useRef(0);
  const isDragging = useRef(false);

  const handleMouseDown = useCallback((event: React.MouseEvent) => {
    startX.current = event.clientX;
    startY.current = event.clientY;
    isDragging.current = false;
  }, []);

  const handleMouseMove = useCallback((event: React.MouseEvent) => {
    const diffX = Math.abs(event.clientX - startX.current);
    const diffY = Math.abs(event.clientY - startY.current);
    
    if (diffX > threshold || diffY > threshold) {
      isDragging.current = true;
    }
  }, []);

  const handleMouseUp = useCallback((event: React.MouseEvent) => {
    if (!isDragging.current) {
      onClick(event);
    }
  }, [onClick]);

  return {
    onMouseDown: handleMouseDown,
    onMouseMove: handleMouseMove,
    onMouseUp: handleMouseUp,
  };
}

export default useDraggableClickHandler;
